/**
 * The report library, server-side.
 *
 * Replaces reading and writing IndexedDB directly. The difference that matters
 * is not durability — it is that a workspace now has ONE shelf. A report a
 * colleague generated is a report you can open, and the client's report
 * survives the laptop it was made on.
 *
 * THE LIST NEVER SELECTS `html`. A library page showing twenty reports would
 * otherwise pull twenty complete documents over the wire to render twenty
 * cards, and the cards show a title, a date and a one-line summary. The
 * document is fetched when somebody actually opens or downloads one, which is
 * also the only time it is needed.
 *
 * IndexedDB is not deleted. It stays as the source for a one-time upload of
 * whatever a person already had locally (see `uploadLocalReports`), and as the
 * thing that still holds those reports if the upload fails. Removing the local
 * copy before the remote one is confirmed would be trading a private shelf for
 * no shelf.
 *
 * @module services/reportService
 */

import { getSupabaseClient, isSupabaseConfigured } from "@/config/supabase";
import { toJson } from "./dbBoundary";
import { logError } from "./logger";
import { readAllRows } from "./supabasePaging";
import type { ServiceResult } from "@/types";
import type { TFn } from "@/utils/channelCorrelation";

/** A library card: everything except the document itself. */
export interface ReportSummary {
  id: string;
  workspaceId: string;
  title: string;
  scenario: string;
  summary?: string;
  /** Null once the author's account is deleted; the email survives. */
  createdBy: string | null;
  createdByEmail: string | null;
  createdAt: string;
}

export interface NewReport {
  workspaceId: string;
  title: string;
  scenario: string;
  summary?: string;
  html: string;
  context?: unknown;
}

/** Columns that make a card. `html` is deliberately absent. */
const CARD_COLUMNS =
  "id, workspace_id, title, scenario, summary, created_by, created_by_email, created_at";

interface Row {
  id: string;
  workspace_id: string;
  title: string;
  scenario: string;
  summary: string | null;
  created_by: string | null;
  created_by_email: string | null;
  created_at: string;
}

const toSummary = (r: Row): ReportSummary => ({
  id: r.id,
  workspaceId: r.workspace_id,
  title: r.title,
  scenario: r.scenario,
  summary: r.summary ?? undefined,
  createdBy: r.created_by,
  createdByEmail: r.created_by_email,
  createdAt: r.created_at,
});

/**
 * Every report on this workspace's shelf, newest first.
 *
 * @returns the cards, or a failure — never a silent empty list, which would
 *   read as "nobody has made one" when the truth is "we could not look"
 */
export const listReports = async (
  workspaceId: string,
  t: TFn,
): Promise<ServiceResult<ReportSummary[]>> => {
  if (!isSupabaseConfigured()) return { success: true, data: [] };

  try {
    // Paged: PostgREST hands back at most 1000 rows with HTTP 200 and no
    // error, so an unpaged read of a shelf past that size lists the newest
    // 1000 and silently drops the rest.
    const { rows, error } = await readAllRows<Row>(
      (from, to) =>
        getSupabaseClient()
          .from("reports")
          .select(CARD_COLUMNS)
          .eq("workspace_id", workspaceId)
          // created_at alone is not unique — two reports generated in one batch
          // share a timestamp, and paging over a non-unique order drops and
          // repeats rows at page boundaries. Same tie-break the audit trail uses.
          .order("created_at", { ascending: false })
          .order("id", { ascending: false })
          .range(from, to),
      "reports shelf",
    );

    if (error) {
      // Never a partial list: shown as complete, it says "these are all the
      // reports" when the truth is "we could not read them all".
      logError("Reports", "Failed to list reports", error);
      const message = (error as { message?: unknown }).message;
      return { success: false, error: typeof message === "string" ? message : t("reports.errUnexpected") };
    }
    return { success: true, data: rows.map(toSummary) };
  } catch (err) {
    logError("Reports", "List reports failed", err);
    return { success: false, error: t("reports.errUnexpected") };
  }
};

/**
 * Fetch one report's document, on demand.
 *
 * @returns the HTML, or a failure. Never an empty string — an empty document
 *   opens as a blank tab, which reads as a broken report rather than a failed
 *   read.
 */
export const fetchReportHtml = async (id: string, t: TFn): Promise<ServiceResult<string>> => {
  if (!isSupabaseConfigured()) return { success: false, error: t("reports.errDemoMode") };

  try {
    const { data, error } = await getSupabaseClient()
      .from("reports")
      .select("html")
      .eq("id", id)
      .maybeSingle();

    if (error) {
      logError("Reports", "Failed to read report html", error);
      return { success: false, error: error.message };
    }
    const html = (data as { html?: string } | null)?.html;
    if (!html) return { success: false, error: t("reports.errNotFound") };
    return { success: true, data: html };
  } catch (err) {
    logError("Reports", "Read report html failed", err);
    return { success: false, error: t("reports.errUnexpected") };
  }
};

/**
 * Put a report on the shelf.
 *
 * `created_by` is filled from the session rather than the caller, because the
 * insert policy requires it to equal auth.uid() — a report claiming an author
 * who did not write it would be refused, and rightly.
 */
export const saveReport = async (report: NewReport, t: TFn): Promise<ServiceResult<ReportSummary>> => {
  if (!isSupabaseConfigured()) return { success: false, error: t("reports.errDemoMode") };

  try {
    const supabase = getSupabaseClient();
    const { data: auth } = await supabase.auth.getUser();
    const user = auth.user;
    if (!user) return { success: false, error: "Not signed in" };

    const { data, error } = await supabase
      .from("reports")
      .insert({
        workspace_id: report.workspaceId,
        created_by: user.id,
        created_by_email: user.email ?? null,
        title: report.title,
        scenario: report.scenario,
        summary: report.summary ?? null,
        html: report.html,
        context: toJson(report.context ?? null),
      })
      .select(CARD_COLUMNS)
      .single();

    if (error) {
      logError("Reports", "Failed to save report", error);
      return { success: false, error: error.message };
    }
    return { success: true, data: toSummary(data as Row) };
  } catch (err) {
    logError("Reports", "Save report failed", err);
    return { success: false, error: t("reports.errUnexpected") };
  }
};

/**
 * Remove a report from the shelf.
 *
 * The policy allows this for the author or a workspace admin only. That is a
 * deliberate change from the browser-local behaviour: deleting used to remove
 * your own copy, and now it removes everyone's.
 */
export const deleteReport = async (id: string, t: TFn): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) return { success: false, error: t("reports.errDemoMode") };

  try {
    const { error } = await getSupabaseClient().from("reports").delete().eq("id", id);
    if (error) {
      logError("Reports", "Failed to delete report", error);
      return { success: false, error: error.message };
    }
    return { success: true, data: undefined };
  } catch (err) {
    logError("Reports", "Delete report failed", err);
    return { success: false, error: t("reports.errUnexpected") };
  }
};

/**
 * Move whatever this browser already holds onto the shared shelf, once.
 *
 * Every report generated before this existed lives in the author's IndexedDB
 * and nowhere else. Left alone they would simply vanish from the product the
 * day the library started reading from the server — the reports would still be
 * on disk, and no screen would ever show them again.
 *
 * WHAT IS UPLOADED IS NOT DELETED LOCALLY. Trading a private shelf for no shelf
 * is the one outcome worse than the problem, so the local copy stays until
 * somebody clears it; a duplicate is cheap and recoverable, a loss is not.
 *
 * Re-running is safe: a marker records that this browser has already offered
 * its reports, and the marker is only set after every upload succeeded.
 *
 * @param workspaceId - only this workspace's local reports are offered
 * @param local - the reports already read out of IndexedDB
 * @returns how many were uploaded, or null when there was nothing to do
 */
const UPLOADED_MARKER = "ydm_mi_reports_uploaded";

const readUploadedIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(UPLOADED_MARKER) ?? "[]") as string[];
  } catch {
    return [];
  }
};

const writeUploadedIds = (ids: string[]): void => {
  try {
    localStorage.setItem(UPLOADED_MARKER, JSON.stringify(ids));
  } catch {
    // A browser refusing storage costs a duplicate on the next visit, not data.
  }
};

/**
 * Record that this browser's copy of a local report is already on the shelf.
 *
 * Called by whoever puts a report on BOTH shelves at generation time. Without
 * it the sweep below has no way to tell a report that was already shared from
 * one that has never been offered, and posts a second copy the first time
 * somebody opens the library in the browser that generated it — on a shelf
 * every colleague is reading. Measured: two rows, not one.
 *
 * The id is the LOCAL (IndexedDB) id, which is what the marker holds and what
 * the sweep compares against; the server's row id is a different value.
 */
export const markReportUploaded = (localId: string): void => {
  const done = readUploadedIds();
  if (done.includes(localId)) return;
  writeUploadedIds([...done, localId]);
};

type LocalReport = { id: string; title: string; scenario: string; summary?: string; html: string; context?: unknown };

/**
 * One sweep per workspace at a time. The marker is read once at the start of a
 * sweep, so two overlapping sweeps each see the same reports as pending and
 * both post them — measured in the browser: StrictMode runs the library's
 * effect twice and the same report landed twice, 0.1ms apart. A second caller
 * gets the sweep already running instead of starting its own.
 */
const inFlight = new Map<string, Promise<number | null>>();

/**
 * The same guarantee between TABS. The map above is per-tab; two tabs opening
 * /reports together each read the shared marker before the other wrote it.
 * Web Locks are shared by every tab of the origin, and the sweep reads the
 * marker only after it holds the lock, so the second tab sees what the first
 * posted. A browser without the API keeps the per-tab guard only.
 */
const acrossTabs = <T>(name: string, fn: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request(name, fn) as Promise<T>;
};

export const uploadLocalReports = (
  workspaceId: string,
  local: LocalReport[],
  t: TFn,
): Promise<number | null> => {
  const running = inFlight.get(workspaceId);
  if (running) return running;
  const sweep = acrossTabs(`ydm-report-sweep:${workspaceId}`, () =>
    sweepLocalReports(workspaceId, local, t),
  ).finally(() => {
    inFlight.delete(workspaceId);
  });
  inFlight.set(workspaceId, sweep);
  return sweep;
};

const sweepLocalReports = async (
  workspaceId: string,
  local: LocalReport[],
  t: TFn,
): Promise<number | null> => {
  if (!isSupabaseConfigured() || local.length === 0) return null;

  const done = readUploadedIds();

  const pending = local.filter((r) => !done.includes(r.id));
  if (pending.length === 0) return null;

  let uploaded = 0;
  for (const r of pending) {
    const result = await saveReport(
      {
        workspaceId,
        title: r.title,
        scenario: r.scenario,
        summary: r.summary,
        html: r.html,
        context: r.context,
      },
      t,
    );
    // Marked one at a time, and written as it lands: a run that fails — or a
    // tab that closes — halfway must not re-upload the half that already
    // landed, which would put duplicates on a shared shelf.
    if (!result.success) break;
    markReportUploaded(r.id);
    uploaded += 1;
  }

  return uploaded;
};
